import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const SERVICES = [
  "Governance, Risk & Compliance (GRC) Frameworks",
  "Operational Resilience Planning",
  "Process Optimization",
  "Compliance Due Diligence",
  "Internal Controls & Governance Support",
  "General Inquiry",
];

const initial = {
  full_name: "",
  company_name: "",
  email: "",
  phone: "",
  service_of_interest: "",
  message: "",
  sms_transactional_consent: false,
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you. Your inquiry has been received.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full border-b border-slate-300 bg-transparent py-3 text-navy placeholder:text-slate-400 focus:border-gold focus:outline-none transition-colors";
  const labelClass = "block text-sm font-medium text-slate-600 mb-1";

  return (
    <div data-testid="contact-page">
      <section className="relative bg-navy">
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-28">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">Contact</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white max-w-4xl">
            Contact Yana Capital Group
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-300 max-w-2xl">
            If your organization is looking for a practical consulting partner focused on
            compliance, governance, and operational resilience, we'd be glad to speak with you.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32 grid lg:grid-cols-12 gap-16">
          {/* Details */}
          <div className="lg:col-span-4">
            <h2 className="text-2xl font-medium text-navy">Get in Touch</h2>
            <ul className="mt-8 space-y-7">
              <li className="flex items-start gap-4">
                <Mail size={22} className="text-gold mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <a href="mailto:ScottWilson@yanacapitalgroup.com" className="text-navy hover:text-gold transition-colors break-all">
                    ScottWilson@yanacapitalgroup.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={22} className="text-gold mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <a href="tel:+16193401668" className="text-navy hover:text-gold transition-colors">(619) 340-1668</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <MapPin size={22} className="text-gold mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-slate-500">Office</p>
                  <p className="text-navy">1223 Cleveland Ave.<br />San Diego, CA 92103</p>
                </div>
              </li>
            </ul>

            <div className="mt-10 border border-slate-200 p-6 bg-slate-50">
              <div className="flex items-center gap-3 text-navy">
                <CalendarClock size={22} className="text-gold" />
                <h3 className="font-medium">Book a Consultation</h3>
              </div>
              <p className="mt-3 text-sm text-slate-600">
                Prefer to schedule directly? Reach out by email or phone and we'll find a time that works for you.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <form data-testid="contact-form" onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <label className={labelClass} htmlFor="full_name">Full Name *</label>
                <input id="full_name" data-testid="contact-full-name" className={inputClass} value={form.full_name} onChange={update("full_name")} placeholder="Jane Doe" />
              </div>
              <div>
                <label className={labelClass} htmlFor="company_name">Company Name</label>
                <input id="company_name" data-testid="contact-company-name" className={inputClass} value={form.company_name} onChange={update("company_name")} placeholder="Company Inc." />
              </div>
              <div>
                <label className={labelClass} htmlFor="email">Email Address *</label>
                <input id="email" type="email" data-testid="contact-email" className={inputClass} value={form.email} onChange={update("email")} placeholder="jane@company.com" />
              </div>
              <div>
                <label className={labelClass} htmlFor="phone">Phone Number</label>
                <input id="phone" data-testid="contact-phone" className={inputClass} value={form.phone} onChange={update("phone")} placeholder="(555) 000-0000" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="service">Service of Interest</label>
                <select id="service" data-testid="contact-service" className={`${inputClass} cursor-pointer`} value={form.service_of_interest} onChange={update("service_of_interest")}>
                  <option value="">Select a service…</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="message">Message *</label>
                <textarea id="message" rows={5} data-testid="contact-message" className={`${inputClass} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us about your priorities…" />
              </div>
              <div className="sm:col-span-2 space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="contact-sms-transactional"
                    checked={form.sms_transactional_consent}
                    onChange={toggle("sms_transactional_consent")}
                    className="mt-1 h-4 w-4 shrink-0 accent-gold cursor-pointer"
                  />
                  <span className="text-sm leading-relaxed text-slate-500">
                    By checking, you agree to receive SMS messages from Yana Capital Group regarding your consultation request, appointment scheduling, and customer support. Message frequency varies. Msg &amp; data rates may apply. Reply HELP for help or STOP to opt out. View our{" "}
                    <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link>{" "}and{" "}
                    <Link to="/terms" className="text-gold hover:underline">Terms of Use</Link>.
                  </span>
                </label>
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  className="bg-gold text-white hover:bg-gold-hover transition-colors duration-200 px-8 py-4 text-sm font-semibold tracking-wide disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Submit Inquiry"}
                </button>
                <p className="mt-4 text-sm text-slate-500">
                  We provide consulting support built around structure, stability, and practical business value.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
